/**
 * Module d'export et de partage pour l'application Bible Study
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Exporter une rubrique en PDF
 */
export const exportRubriqueToPDF = async (rubriqueNumber, rubriqueTitle, content) => {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - (margin * 2);
    
    // Titre du document
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Rubrique ${rubriqueNumber} : ${rubriqueTitle}`, margin, 20);
    
    // Date
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    const date = new Date().toLocaleDateString('fr-FR');
    pdf.text(`Généré le ${date}`, margin, 28);
    
    // Ligne de séparation
    pdf.setLineWidth(0.5);
    pdf.line(margin, 32, pageWidth - margin, 32);
    
    // Contenu - Nettoyer le HTML et extraire le texte
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const textContent = tempDiv.innerText || tempDiv.textContent || '';
    
    // Découper le texte en lignes
    pdf.setFontSize(11);
    const lines = pdf.splitTextToSize(textContent, maxWidth);
    
    let y = 40;
    lines.forEach((line, index) => {
      if (y > pageHeight - margin) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(line, margin, y);
      y += 6;
    });
    
    // Footer
    const totalPages = pdf.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.text(
        `Page ${i} / ${totalPages} - Bible Study App`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }
    
    // Sauvegarder
    const filename = `Rubrique_${rubriqueNumber}_${rubriqueTitle.replace(/[^a-z0-9]/gi, '_')}.pdf`;
    pdf.save(filename);
    
    return { success: true, filename };
  } catch (error) {
    console.error('Erreur export PDF:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Exporter toutes les rubriques en un seul PDF
 */
export const exportAllRubriquesToPDF = async (rubriques) => {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - (margin * 2);
    
    // Page de garde
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Étude Biblique Complète', pageWidth / 2, 40, { align: 'center' });
    
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'normal');
    const date = new Date().toLocaleDateString('fr-FR');
    pdf.text(`Généré le ${date}`, pageWidth / 2, 50, { align: 'center' });
    
    pdf.setFontSize(12);
    pdf.text(`${rubriques.length} rubriques`, pageWidth / 2, 60, { align: 'center' });
    
    // Ajouter chaque rubrique
    for (let i = 0; i < rubriques.length; i++) {
      const rubrique = rubriques[i];
      
      // Nouvelle page pour chaque rubrique
      pdf.addPage();
      
      // Titre de la rubrique
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Rubrique ${rubrique.number} : ${rubrique.title}`, margin, 20);
      
      // Ligne de séparation
      pdf.setLineWidth(0.5);
      pdf.line(margin, 25, pageWidth - margin, 25);
      
      // Contenu
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = rubrique.content;
      const textContent = tempDiv.innerText || tempDiv.textContent || '';
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const lines = pdf.splitTextToSize(textContent, maxWidth);
      
      let y = 32;
      lines.forEach((line) => {
        if (y > pageHeight - margin) {
          pdf.addPage();
          y = margin;
        }
        pdf.text(line, margin, y);
        y += 6;
      });
    }
    
    // Footer sur toutes les pages
    const totalPages = pdf.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.text(
        `Page ${i} / ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }
    
    // Sauvegarder
    const filename = `Etude_Biblique_Complete_${date.replace(/\//g, '-')}.pdf`;
    pdf.save(filename);
    
    return { success: true, filename, count: rubriques.length };
  } catch (error) {
    console.error('Erreur export PDF complet:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Copier le contenu dans le presse-papier
 */
export const copyToClipboard = async (content) => {
  try {
    // Nettoyer le HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const textContent = tempDiv.innerText || tempDiv.textContent || '';
    
    // Copier
    await navigator.clipboard.writeText(textContent);
    return { success: true };
  } catch (error) {
    // Fallback pour navigateurs anciens
    try {
      const textArea = document.createElement('textarea');
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      textArea.value = tempDiv.innerText || tempDiv.textContent || '';
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return { success: true };
    } catch (fallbackError) {
      console.error('Erreur copie presse-papier:', fallbackError);
      return { success: false, error: fallbackError.message };
    }
  }
};

/**
 * Partager via l'API native (mobile)
 */
export const shareContent = async (rubriqueNumber, rubriqueTitle, content) => {
  try {
    // Vérifier si l'API Share est disponible
    if (!navigator.share) {
      throw new Error('API Share non disponible sur ce navigateur');
    }
    
    // Nettoyer le contenu
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const textContent = tempDiv.innerText || tempDiv.textContent || '';
    
    // Limiter la longueur pour le partage
    const maxLength = 5000;
    const truncatedContent = textContent.length > maxLength 
      ? textContent.substring(0, maxLength) + '...' 
      : textContent;
    
    await navigator.share({
      title: `Rubrique ${rubriqueNumber} : ${rubriqueTitle}`,
      text: truncatedContent,
    });
    
    return { success: true };
  } catch (error) {
    console.error('Erreur partage:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Exporter en format texte
 */
export const exportToText = (rubriqueNumber, rubriqueTitle, content) => {
  try {
    // Nettoyer le HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const textContent = tempDiv.innerText || tempDiv.textContent || '';
    
    // Créer le fichier texte
    const header = `RUBRIQUE ${rubriqueNumber} : ${rubriqueTitle}\n`;
    const date = `Généré le ${new Date().toLocaleDateString('fr-FR')}\n`;
    const separator = '='.repeat(60) + '\n\n';
    
    const fullContent = header + date + separator + textContent;
    
    // Télécharger
    const blob = new Blob([fullContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Rubrique_${rubriqueNumber}_${rubriqueTitle.replace(/[^a-z0-9]/gi, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Erreur export texte:', error);
    return { success: false, error: error.message };
  }
};

export default {
  exportRubriqueToPDF,
  exportAllRubriquesToPDF,
  copyToClipboard,
  shareContent,
  exportToText
};
